import { Activity } from "lucide-react";

export interface CrmContactActivityTypeOption {
  value: string;
  label: string;
}

export interface CrmContactActivityLogDraft {
  type: string;
  performedAt: string;
  subject: string;
  body: string;
}

interface CrmContactActivityLogCardProps {
  draft: CrmContactActivityLogDraft;
  isLogging: boolean;
  feedback: string | null;
  error: string | null;
  onDraftChange: (draft: CrmContactActivityLogDraft) => void;
  onLog: () => void;
  activityTypes?: readonly CrmContactActivityTypeOption[];
}

export const CRM_CONTACT_ACTIVITY_TYPE_OPTIONS: readonly CrmContactActivityTypeOption[] = [
  { value: "call", label: "Call" },
  { value: "meeting", label: "Meeting" },
  { value: "note", label: "Note" },
  { value: "email", label: "Email" },
];

export function getDefaultCrmActivityTimestamp() {
  const now = new Date();
  now.setSeconds(0, 0);
  const timezoneOffset = now.getTimezoneOffset();
  const local = new Date(now.getTime() - timezoneOffset * 60_000);
  return local.toISOString().slice(0, 16);
}

export function CrmContactActivityLogCard({
  draft,
  isLogging,
  feedback,
  error,
  onDraftChange,
  onLog,
  activityTypes = CRM_CONTACT_ACTIVITY_TYPE_OPTIONS,
}: CrmContactActivityLogCardProps) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <div className="flex items-center gap-2">
        <Activity className="h-4 w-4 text-[var(--color-primary)]" />
        <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Log Activity</h3>
      </div>
      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
        Add a manual call, meeting, note, or email touchpoint through the mounted CRM activity route.
      </p>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Type</span>
          <select
            value={draft.type}
            onChange={(event) => onDraftChange({ ...draft, type: event.target.value })}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          >
            {activityTypes.map((activityType) => (
              <option key={activityType.value} value={activityType.value}>{activityType.label}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">When</span>
          <input
            type="datetime-local"
            value={draft.performedAt}
            onChange={(event) => onDraftChange({ ...draft, performedAt: event.target.value })}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          />
        </label>
        <label className="space-y-1 text-sm md:col-span-2">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Subject</span>
          <input
            type="text"
            value={draft.subject}
            onChange={(event) => onDraftChange({ ...draft, subject: event.target.value })}
            placeholder="Manual note"
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          />
        </label>
        <label className="space-y-1 text-sm md:col-span-2">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Description</span>
          <textarea
            value={draft.body}
            onChange={(event) => onDraftChange({ ...draft, body: event.target.value })}
            placeholder="Capture what happened so the next teammate has context."
            rows={3}
            className="w-full resize-none rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          />
        </label>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => { onLog(); }}
          disabled={isLogging}
          className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] transition-colors hover:bg-[var(--color-primary-hover)] disabled:opacity-60"
        >
          {isLogging ? "Logging..." : "Log activity"}
        </button>
        {feedback ? <p className="text-xs text-[var(--color-success)]">{feedback}</p> : null}
        {error ? <p className="text-xs text-[var(--color-destructive)]">{error}</p> : null}
      </div>
    </div>
  );
}
